import ProgressStepper from "components/Common/Stepper/Stepper";
import {
	NewOrderDetailsGrid,
	NewOrdersHeaderContainer,
	NewOrdersHeaderHead,
	NewOrdersHeaderRow,
	NewOrdersSummaryCounts,
	NewOrdersSummaryDetails,
	OrderTypeContainerMobile,
	OrderTypeMobile,
	OriginalOrderDetailsAccordion
} from "../NewOrders.styles";
import {
	AreYouSureText,
	CustomColorText,
	GeneratingPDFText,
	ItemsWillClearText,
	NoContinueOrdering,
	NoExitOrder,
	OrderTypeText,
	OriginalOrderDetailsText,
	PDFDownloadText,
	PDFErrorText,
	SaveDraftBeforeLeavingText,
	StartOver,
	UnavailableDataPlaceholderText,
	YesExitOrder,
	YesSaveDraft
} from "constants/text";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import {
	getConfigurationGroupsByProductLine,
	getNewOrderSummaryDetailInfo
} from "components/NewOrders/utils/NewOrderUtils.tsx";
import {
	ConfirmationText,
	DraftExitOrder,
	DraftSaveAndExit,
	NewOrdersHeaderSummaryDetailsAccessoriesCount,
	NewOrdersHeaderSummaryDetailsCabinetsCount,
	NewOrdersHeaderSummaryDetailsCubeCount,
	NewOrdersHeaderSummaryDetailsOrderType,
	NewOrdersHeaderSummaryDetailsProductLines,
	NewOrderThanksHeader,
	OrderDefaultText,
	OrderReviewText,
	PriceEstimateText,
	SaveDraftHangTightText,
	SaveDraftSuccessfullySavedText,
	SelectOrderType
} from "../constants";
import { LinkButton } from "../../Common/Link";
import { useNavigate, useParams } from "react-router";
import { newOrderActions } from "features/reducers/newOrder/newOrder.ts";
import SaveDraftModal from "../SaveDraft/SaveDraftModal";
import ExportButtons from "components/ExportFile/ExportButtons";
import useNewOrdersPDFGenerator from "hooks/useNewOrdersPDFGenerator";
import useSnackbarEffect from "hooks/useSnackbarEffect";
import { models } from "types/api/viewModels.ts";
import PlaceOrderOriginalOrderDetails from "components/Common/PlaceOrderOriginalOrderDetails/PlaceOrderOriginalOrderDetails";
import useInitOrderDetail from "hooks/useInitOrderDetail";
import { AccordionDetails, AccordionSummary, FormControl } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { epsilonRounding } from "utils/epsilonRounding.ts";
import AutoComplete from "../../Common/Autocomplete/Autocomplete.tsx";
import { OrderType, UserChannel, UserRole } from "types/declarations/enums.ts";
import { capitalizeFirstLetter, convertToTitleCase, getNewOrderTitle } from "utils/string.ts";
import Snackbar from "components/Common/SnackBar/SnackBar.tsx";
import ConfirmationDialog from "components/Common/ConfirmationDialog/ConfirmationDialog.tsx";
import { useUserLocation } from "hooks/useUserLocation.ts";
import { useSaveDraftOrderMutation } from "features/api/orderApi.ts";
import { useDialog } from "hooks/useDialog.ts";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import SupportCTA from "components/Common/SupportCTA/SupportCTA";
import { getSupportFormUrl } from "utils/supportForm";
import { PageHeaderVariant } from "components/Common/Page";

interface NewOrdersHeaderProps {
	includeDetails?: boolean;
	includeStepper?: boolean;
	isReview?: boolean;
	isConfirmation?: boolean;
	isPDFExport?: boolean;
	isExcelExport?: boolean;
	draftOrder?: models["DraftOrderViewModel"];
	isAddOn?: boolean;
	handleSetIsLowesUser?: (value: boolean) => void;
	handleSetIsInternalDesigner?: (value: boolean) => void;
	variant?: PageHeaderVariant;
}

interface NewOrdersHeaderTextOptions {
	isProQuote: boolean;
	isConfirmation: boolean;
	isReview: boolean;
}

const getNewOrdersHeaderText = ({ isProQuote, isConfirmation, isReview }: NewOrdersHeaderTextOptions): string => {
	if (isProQuote) return PriceEstimateText;
	if (isConfirmation) return ConfirmationText;
	if (isReview) return OrderReviewText;

	return PriceEstimateText;
};

interface SupportButtonVisibilityOptions {
	isAddOnPage: boolean;
	isAddOnLandingPage: boolean;
	isNewQuotePage: boolean;
	isNewOrderPage: boolean;
	isNewOrderLandingPage: boolean;
	isCustomColor: boolean;
}

const shouldShowSupportButton = ({
	isAddOnPage,
	isAddOnLandingPage,
	isNewQuotePage,
	isNewOrderPage,
	isNewOrderLandingPage,
	isCustomColor
}: SupportButtonVisibilityOptions): boolean =>
	(isAddOnPage && !isAddOnLandingPage) ||
	(!isNewQuotePage && ((isNewOrderPage && !isNewOrderLandingPage) || isCustomColor));

interface ExitRouteOptions {
	dialogLeavePath?: string | null;
	isAddOn: boolean;
	orderId?: string;
	isInternalDesigner: boolean;
}

const getExitRoute = ({ dialogLeavePath, isAddOn, orderId, isInternalDesigner }: ExitRouteOptions): string => {
	if (dialogLeavePath) return dialogLeavePath;
	if (isAddOn) return `/add-ons/${orderId}`;
	if (isInternalDesigner) return "/new-quote";

	return "/new-order";
};

const NewOrdersHeader = ({
	includeDetails = true,
	includeStepper = true,
	isReview = false,
	isConfirmation = false,
	isPDFExport = false,
	isExcelExport = false,
	draftOrder,
	isAddOn = false,
	handleSetIsLowesUser,
	handleSetIsInternalDesigner,
	variant = "panel"
}: NewOrdersHeaderProps) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { dialogLeavePath } = useDialog();
	const dialogState = useSelector((state: RootState) => state.newOrder.leaveNewOrderFlow);
	const parsedCSV = useSelector((state: RootState) => state.newOrder.parsedCSV);
	const orderType = useSelector((state: RootState) => state.newOrder.newOrderDetails?.orderType);
	const newOrderDetails = useSelector((state: RootState) => state.newOrder.newOrderDetails);
	const validatedOrderResponse = useSelector((state: RootState) => state.newOrder.validatedOrderResponse);
	const isCareUser = useSelector((state: RootState) => state.impersonation.isCareUser);
	const {
		isCustomColorPage: isCustomColor,
		isNewOrderPage,
		isNewOrderBuildOrderPage,
		isAddOnPage,
		isAddOnLandingPage,
		isNewOrderLandingPage,
		isAddOnReviewPage,
		isNewQuotePage
	} = useUserLocation();

	const summaryDetails = useMemo(() => {
		return getNewOrderSummaryDetailInfo(draftOrder ?? parsedCSV?.[0], validatedOrderResponse);
	}, [draftOrder, parsedCSV, validatedOrderResponse]);

	const { data: userDetails } = useGetUserDetailsQuery();
	const isLowesUser = userDetails?.userChannel === UserChannel.LOWES;
	const isInternalDesigner = userDetails?.role === UserRole.INTERNAL_DESIGNER;

	useEffect(() => {
		handleSetIsLowesUser?.(isLowesUser);
	}, [isLowesUser, handleSetIsLowesUser]);

	useEffect(() => {
		handleSetIsInternalDesigner?.(isInternalDesigner);
	}, [isInternalDesigner, handleSetIsInternalDesigner]);

	const params = useParams();
	const { orderDetail, orderId } = useInitOrderDetail(params.orderId);
	const showSupportButton = shouldShowSupportButton({
		isAddOnPage,
		isAddOnLandingPage,
		isNewQuotePage,
		isNewOrderPage,
		isNewOrderLandingPage,
		isCustomColor
	});
	const supportFormUrl = getSupportFormUrl({
		type: "Order Help",
		brand:
			orderDetail?.configurations?.[0]?.globals?.brand?.description ??
			newOrderDetails?.configurations?.[0]?.globals?.brand?.description,
		poNumber: orderDetail?.poNumber ?? newOrderDetails?.poNumber
	});
	const uniqueProductLines = summaryDetails?.productLines
		?.split(", ")
		.filter((productLine) => productLine.trim() !== "");

	const isProQuote = orderType === OrderType.QUOTE_ORDER || draftOrder?.orderType === OrderType.QUOTE_ORDER;

	const headerText = getNewOrdersHeaderText({
		isProQuote,
		isConfirmation,
		isReview
	});

	const groupedConfigs = getConfigurationGroupsByProductLine(validatedOrderResponse?.configurations);

	const { pdfData, generateNewOrdersPDF } = useNewOrdersPDFGenerator(
		groupedConfigs,
		headerText,
		validatedOrderResponse?.pricing?.extendedNetPrice ?? validatedOrderResponse?.pricing?.extendedPrice ?? 0,
		newOrderDetails,
		isReview,
		isConfirmation,
		orderDetail,
		isAddOn,
		isCustomColor,
		parsedCSV,
		validatedOrderResponse,
		userDetails?.role
	);
	const { snackbarOpen } = useSnackbarEffect(pdfData.loading);

	const resetReduxData = (resetFlow = true) => {
		dispatch(newOrderActions.clearNewOrder());
		if (resetFlow) dispatch(newOrderActions.resetNewOrderFlow());
		dispatch(newOrderActions.clearTempParsedCSV());
	};

	const handleStartOver = () => {
		if (parsedCSV || newOrderDetails) {
			dispatch(newOrderActions.leaveNewOrderFlow({ state: "pause" }));
		} else {
			resetReduxData();
			navigate(`/${isAddOn ? "add-ons" : "new-order"}${isAddOn ? "/" + orderId : ""}`);
		}
	};

	const handleDialogExitOrder = () => {
		if (dialogState?.navItem) {
			dispatch(newOrderActions.continueNewOrderFlow());
		} else {
			resetReduxData();
			navigate(
				getExitRoute({
					dialogLeavePath,
					isAddOn,
					orderId,
					isInternalDesigner
				})
			);
		}
		resetReduxData(!dialogState?.navItem);
	};

	const handleDialogClose = () => {
		dispatch(newOrderActions.resetNewOrderFlow());
	};

	const [draftModalIsOpen, setDraftModalIsOpen] = useState(false);
	const [saveDraftOrder, { isLoading: isSavingDraft }] = useSaveDraftOrderMutation();
	const draftError = useSelector((state: RootState) => state.newOrder.draftDetails?.draftError);
	const { snackbarOpen: draftSnackbarOpen } = useSnackbarEffect(isSavingDraft, draftError);

	const handleOpenCloseSaveDraftModalToggle = () => {
		if (draftModalIsOpen) {
			handleDialogExitOrder();
		} else {
			setDraftModalIsOpen(true);
			dispatch(newOrderActions.resetNewOrderFlow());
		}
	};

	const handleSaveDraft = async (draft: models["DraftOrderViewModel"]) => {
		try {
			await saveDraftOrder(draft).unwrap();
			// After successful save, navigate away after a brief delay
			setTimeout(() => {
				handleDialogExitOrder();
			}, 1000);
		} catch (error: any) {
			dispatch(newOrderActions.updateDraftError(error));
		}
	};

	const countsByProductLine = validatedOrderResponse?.countsByProductLine ?? {};
	const originalOrderTypes = [OrderType.ORIGINAL, "Display", "Sample"];

	const originalOrderTypesWithAliases = originalOrderTypes.map((orderTypeOption) =>
		orderTypeOption === OrderType.ORIGINAL ? OrderDefaultText : orderTypeOption
	);

	const handleOrderOptionChange = (value: string) => {
		const actualValue = value === OrderDefaultText ? OrderType.ORIGINAL : value;

		dispatch(newOrderActions.updateOrderType(actualValue));
	};

	return (
		<>
			<NewOrdersHeaderContainer
				variant={variant}
				data-testid="new-orders-header"
				isConfirmation={isConfirmation}
				isCareUser={isCareUser}
			>
				{!isNewOrderLandingPage && (
					<NewOrdersHeaderHead>
						{isConfirmation ? (
							<h5>{NewOrderThanksHeader}</h5>
						) : (
							<div data-testid="new-orders-header-start-over">
								<LinkButton
									hasBackIcon
									onClick={handleStartOver}
									data-testid="new-order-start-over-link"
								>
									{StartOver}
								</LinkButton>
							</div>
						)}
						<div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
							{showSupportButton && (
								<SupportCTA
									href={supportFormUrl}
									data-testid="new-orders-support-button"
									sx={{ height: 30 }}
								/>
							)}
							{isPDFExport && (
								<div>
									<ExportButtons
										largePDF={false}
										generatePDF={generateNewOrdersPDF}
										buttonHeader={"EXPORT " + headerText.toUpperCase()}
										isOnlyPDF
									/>
									<Snackbar
										open={snackbarOpen}
										isLoading={pdfData.loading}
										loadingText={GeneratingPDFText}
										successText={PDFDownloadText}
										errorText={PDFErrorText}
										isError={Boolean(pdfData.error)}
									/>
								</div>
							)}
							{isExcelExport && !isInternalDesigner && (
								<div>
									<ExportButtons
										largePDF={false}
										generatePDF={generateNewOrdersPDF}
										buttonHeader={"EXPORT " + headerText.toUpperCase()}
										isOnlyExcel
									/>
								</div>
							)}
						</div>
					</NewOrdersHeaderHead>
				)}

				<ConfirmationDialog
					cancelText={isAddOn ? YesExitOrder : NoExitOrder}
					open={dialogState !== undefined && !draftModalIsOpen}
					onCancel={handleDialogExitOrder}
					onClose={handleDialogClose}
					onConfirm={isAddOn ? handleDialogClose : handleOpenCloseSaveDraftModalToggle}
					confirmText={isAddOn ? NoContinueOrdering : YesSaveDraft}
					subtitle={ItemsWillClearText}
					title={isAddOn ? AreYouSureText : SaveDraftBeforeLeavingText}
				/>

				<SaveDraftModal
					draftOrder={draftOrder}
					saveDraftModalIsOpen={draftModalIsOpen}
					closeModal={() => setDraftModalIsOpen(false)}
					firstButtonText={DraftSaveAndExit}
					secondButtonText={DraftExitOrder}
					isExitingOrder
					onSubmit={handleSaveDraft}
				/>

				{!isConfirmation && (
					<NewOrdersHeaderRow data-testid="new-orders-header-heading">
						<h1 data-testid="new-orders-header-title">
							{getNewOrderTitle(isAddOn, isCustomColor, isLowesUser, isInternalDesigner)}
						</h1>
						{includeStepper && <ProgressStepper data-testid="new-orders-header-stepper" />}
					</NewOrdersHeaderRow>
				)}
				{includeDetails && (
					<NewOrdersSummaryDetails data-testid="new-orders-header-summary-details">
						<NewOrderDetailsGrid>
							<div>{NewOrdersHeaderSummaryDetailsProductLines}</div>
							<div>{NewOrdersHeaderSummaryDetailsCabinetsCount}</div>
							<div>{NewOrdersHeaderSummaryDetailsAccessoriesCount}</div>
							<div>{NewOrdersHeaderSummaryDetailsCubeCount}</div>
							{!isAddOn && !isNewOrderBuildOrderPage && (
								<div>{NewOrdersHeaderSummaryDetailsOrderType}</div>
							)}
							{!isAddOn && isNewOrderBuildOrderPage && !isCustomColor && !isNewQuotePage && (
								<FormControl
									size="small"
									required
									fullWidth
								>
									<AutoComplete
										options={originalOrderTypesWithAliases}
										getOptionLabel={(option) => option}
										onChange={(_event, value) => handleOrderOptionChange(value)}
										value={
											orderType === OrderType.ORIGINAL || orderType === OrderType.UNKNOWN
												? OrderDefaultText
												: capitalizeFirstLetter(orderType)
										}
										isError={false}
										errorText={undefined}
										label={SelectOrderType}
										dataTestId="newOrder-select-order-type-dropdown"
										disableClearable
									/>
								</FormControl>
							)}
						</NewOrderDetailsGrid>
						{validatedOrderResponse?.countsByProductLine ? (
							<>
								{Object.entries(countsByProductLine).map(([productLine, counts], index) => (
									<NewOrderDetailsGrid
										key={productLine}
										isNewOrderPage={isNewOrderLandingPage}
									>
										<div>{productLine}</div>
										<div data-testid="new-orders-header-summary-details-cabinets-count">
											{counts?.cabinetCount ?? UnavailableDataPlaceholderText}
										</div>

										<div data-testid="new-orders-header-summary-details-accessories-count">
											{counts?.accessoryCount ?? UnavailableDataPlaceholderText}
										</div>

										<div data-testid="new-orders-header-summary-details-cubes-count">
											{epsilonRounding(
												Number(counts?.cubeCount ?? UnavailableDataPlaceholderText)
											)
												.toFixed(2)
												.toString()}
										</div>
										{((!isAddOn && !isNewOrderBuildOrderPage) || isNewQuotePage) && index === 0 && (
											<div data-testid="new-orders-header-summary-order-type">
												{convertToTitleCase(orderType)}
											</div>
										)}
									</NewOrderDetailsGrid>
								))}
								{Object.entries(countsByProductLine).map(([productLine, counts]) => (
									<NewOrdersSummaryCounts key={productLine}>
										<div>
											<div>{NewOrdersHeaderSummaryDetailsCabinetsCount}</div>
											<div>{counts?.cabinetCount ?? UnavailableDataPlaceholderText}</div>
										</div>
										<div>
											<div>{NewOrdersHeaderSummaryDetailsAccessoriesCount}</div>
											<div>{counts?.accessoryCount ?? UnavailableDataPlaceholderText}</div>
										</div>
										<div>
											<div>{NewOrdersHeaderSummaryDetailsCubeCount}</div>
											<div>
												{epsilonRounding(
													Number(counts?.cubeCount ?? UnavailableDataPlaceholderText)
												)
													.toFixed(2)
													.toString()}
											</div>
										</div>
									</NewOrdersSummaryCounts>
								))}
								<OrderTypeMobile>
									<div>{NewOrdersHeaderSummaryDetailsOrderType}</div>
									<div>{capitalizeFirstLetter(isNewQuotePage ? "Quote Order" : orderType)}</div>
								</OrderTypeMobile>
							</>
						) : (
							<>
								{uniqueProductLines?.map((productLine) => (
									<NewOrderDetailsGrid
										data-testid="new-orders-header-summary-details-product-lines"
										key={productLine}
									>
										<div>{productLine}</div>
										<div>{UnavailableDataPlaceholderText}</div>
										<div>{UnavailableDataPlaceholderText}</div>
										<div>{UnavailableDataPlaceholderText}</div>
										{isCustomColor && (
											<div data-testid="custom-color-order-type">{CustomColorText}</div>
										)}
										{isNewQuotePage && <div data-testid="quote-order-type">Quote Order</div>}
									</NewOrderDetailsGrid>
								))}
								{uniqueProductLines?.map((productLine) => (
									<NewOrdersSummaryCounts key={productLine}>
										<div>
											<div>{NewOrdersHeaderSummaryDetailsCabinetsCount}</div>
											<div data-testid="new-orders-header-summary-details-cabinets-count">
												{UnavailableDataPlaceholderText}
											</div>
										</div>
										<div>
											<div>{NewOrdersHeaderSummaryDetailsAccessoriesCount}</div>
											<div data-testid="new-orders-header-summary-details-cubes-count">
												{UnavailableDataPlaceholderText}
											</div>
										</div>
										<div>
											<div>{NewOrdersHeaderSummaryDetailsCubeCount}</div>
											<div data-testid="new-orders-header-summary-details-accessories-count">
												{UnavailableDataPlaceholderText}
											</div>
										</div>
										{isCustomColor && (
											<div>
												<div>{OrderTypeText}</div>
												<div data-testid="custom-color-order-type">{CustomColorText}</div>
											</div>
										)}
										{isNewQuotePage && (
											<div>
												<div>{OrderTypeText}</div>
												<div data-testid="quote-order-type">Quote Order</div>
											</div>
										)}
									</NewOrdersSummaryCounts>
								))}
								<OrderTypeContainerMobile>
									{!isAddOn && !isCustomColor && (
										<FormControl
											size="small"
											required
											fullWidth
										>
											<AutoComplete
												options={originalOrderTypesWithAliases}
												getOptionLabel={(option) => option}
												onChange={(_event, value) => handleOrderOptionChange(value)}
												value={
													orderType === OrderType.ORIGINAL || orderType === OrderType.UNKNOWN
														? OrderDefaultText
														: capitalizeFirstLetter(orderType)
												}
												isError={false}
												errorText={undefined}
												label={SelectOrderType}
												dataTestId="newOrder-select-order-type-dropdown"
												disableClearable
											/>
										</FormControl>
									)}
								</OrderTypeContainerMobile>
							</>
						)}
					</NewOrdersSummaryDetails>
				)}
				{isAddOn && !isConfirmation && (
					<OriginalOrderDetailsAccordion defaultExpanded={isAddOnReviewPage}>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>{OriginalOrderDetailsText}</AccordionSummary>
						<AccordionDetails>
							<PlaceOrderOriginalOrderDetails orderDetail={orderDetail} />
						</AccordionDetails>
					</OriginalOrderDetailsAccordion>
				)}
			</NewOrdersHeaderContainer>
			<Snackbar
				open={draftSnackbarOpen}
				isLoading={isSavingDraft}
				loadingText={SaveDraftHangTightText}
				successText={SaveDraftSuccessfullySavedText}
				isError={draftError !== undefined}
				errorText={draftError ?? ""}
			/>
		</>
	);
};

export default NewOrdersHeader;
